import {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useCallback,
  KeyboardEvent,
  useId,
  useMemo,
  type CSSProperties
} from "react";
import classNames from "classnames";
import Icon from "@/components/Icon";
import { handleKeyDown } from "./keyboardHandlers";
import { useFieldsetDisabled } from "@/hooks/useFieldsetDisabled";

type SelectOption = {
  disabled?: boolean;
  value: string;
  border?: boolean;
  displayText?: string;
  label?: string;
};

type OptionListMaxHeight = "default" | "extended" | "none";

export type SelectProps = {
  label?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  supportingText?: string;
  options: readonly SelectOption[];
  onSelect?(v: string): void;
  onClear?(): void;
  value?: string;
  className?: string;
  "data-testid"?: string;
  optionListMaxHeight?: OptionListMaxHeight;
  placeholder?: string;
  dropdownHeight?: "full" | "default" | "short";
  dropdownPlacement?: SelectDropdownPlacement;
  searchable?: boolean;
};

export type SelectDropdownPlacement = "above" | "below";

function optionLabel(option: SelectOption) {
  return option.label || option.displayText || option.value;
}

function filterSelectOptions(options: readonly SelectOption[], query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return options;
  }

  return options.filter((option) => {
    return (
      optionLabel(option).toLowerCase().includes(normalizedQuery) ||
      option.value.toLowerCase().includes(normalizedQuery)
    );
  });
}

const dropdownHeightStyles: Record<"full" | "default" | "short", string> = {
  full: "",
  default: "max-h-60 overflow-auto",
  short: "max-h-40 overflow-auto"
};

const dropdownMaxHeights = {
  full: undefined,
  default: 240,
  short: 160
} as const;

type DropdownPosition = {
  left: number;
  width: number;
  placement: SelectDropdownPlacement;
  maxHeight: number;
  top?: number;
  bottom?: number;
};

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      name,
      required,
      disabled: disabledProp = false,
      error,
      errorText,
      supportingText,
      options,
      onSelect,
      onClear,
      className,
      value,
      placeholder,
      dropdownHeight = "default",
      dropdownPlacement = "below",
      searchable = false,
      "data-testid": testId = "select"
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [searchQuery, setSearchQuery] = useState("");
    const { ref: containerRef, disabled: fieldsetDisabled } = useFieldsetDisabled<HTMLDivElement>();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const comboboxRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);
    const selectedOption = options.find((opt) => opt.value === value);
    const selectedLabel = selectedOption ? optionLabel(selectedOption) : "";
    const visibleOptions = useMemo(
      () => (searchable ? filterSelectOptions(options, searchQuery) : options),
      [options, searchQuery, searchable]
    );
    const id = useId();
    const listboxId = `${id}-listbox`;
    const disabled = disabledProp || fieldsetDisabled;
    const isOpenRef = useRef(false);
    const suppressOpenOnFocusRef = useRef(false);
    const hasSelectedValue = (value?.length ?? 0) > 0;
    const showClearButton = Boolean(onClear) && hasSelectedValue && !disabled;

    const closeDropdown = () => {
      setIsOpen(false);
      setSearchQuery("");
    };

    const updateDropdownPosition = useCallback(() => {
      if (dropdownPlacement !== "above" || !dropdownRef.current) return;

      const rect = dropdownRef.current.getBoundingClientRect();
      const gap = 4;
      const viewportPadding = 8;
      const spaceAbove = Math.max(rect.top - viewportPadding, 0);
      const spaceBelow = Math.max(window.innerHeight - rect.bottom - viewportPadding, 0);
      const placement = spaceAbove >= spaceBelow ? "above" : "below";
      const availableSpace = placement === "above" ? spaceAbove : spaceBelow;
      const configuredMaxHeight = dropdownMaxHeights[dropdownHeight];
      const left = Math.min(
        Math.max(rect.left, viewportPadding),
        Math.max(viewportPadding, window.innerWidth - rect.width - viewportPadding)
      );

      setDropdownPosition({
        left,
        width: rect.width,
        placement,
        maxHeight: configuredMaxHeight ? Math.min(configuredMaxHeight, availableSpace) : availableSpace,
        top: placement === "below" ? rect.bottom + gap : undefined,
        bottom: placement === "above" ? window.innerHeight - rect.top + gap : undefined
      });
    }, [dropdownHeight, dropdownPlacement]);

    useEffect(() => {
      if (!isOpen || dropdownPlacement !== "above") {
        setDropdownPosition(null);
        return;
      }

      updateDropdownPosition();
      window.addEventListener("resize", updateDropdownPosition);
      window.addEventListener("scroll", updateDropdownPosition, true);

      return () => {
        window.removeEventListener("resize", updateDropdownPosition);
        window.removeEventListener("scroll", updateDropdownPosition, true);
      };
    }, [dropdownPlacement, isOpen, updateDropdownPosition]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          setSearchQuery("");
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
      const justOpened = isOpen && !isOpenRef.current;
      isOpenRef.current = isOpen;

      if (!isOpen) {
        setFocusedIndex(-1);
        return;
      }

      if (!justOpened) {
        return;
      }

      const selectedIndex = visibleOptions.findIndex((opt) => opt.value === value);
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : visibleOptions.length > 0 ? 0 : -1);
      if (searchable) {
        searchInputRef.current?.focus();
      }
    }, [isOpen, searchable, value, visibleOptions]);

    useEffect(() => {
      if (!isOpen || focusedIndex < 0) return;
      const focusedElement = optionRefs.current[focusedIndex];
      if (!focusedElement) return;

      // Scroll only inside the dropdown's listbox, not the page. `scrollIntoView`
      // on the option bubbles to the nearest scrollable ancestor, which can be
      // the page when the listbox itself isn't overflowing — that's how this
      // component used to scroll the whole page when the Select opened.
      const listbox = focusedElement.parentElement;
      if (!listbox) return;

      const optionTop = focusedElement.offsetTop;
      const optionBottom = optionTop + focusedElement.offsetHeight;
      const visibleTop = listbox.scrollTop;
      const visibleBottom = visibleTop + listbox.clientHeight;

      if (optionTop < visibleTop) {
        listbox.scrollTop = optionTop;
      } else if (optionBottom > visibleBottom) {
        listbox.scrollTop = optionBottom - listbox.clientHeight;
      }
    }, [focusedIndex, isOpen]);

    const syncNativeSelectValue = (optionValue: string) => {
      const nativeSelect = ref && "current" in ref ? ref.current : null;
      if (nativeSelect) {
        const event = new Event("change", { bubbles: true });
        nativeSelect.value = optionValue;
        nativeSelect.dispatchEvent(event);
      }
    };

    const handleSelect = (optionValue: string) => {
      if (disabled) return;

      syncNativeSelectValue(optionValue);
      onSelect?.(optionValue);
      closeDropdown();
    };

    const handleClear = () => {
      if (disabled || !onClear) return;

      syncNativeSelectValue("");
      onSelect?.("");
      onClear();
      closeDropdown();
      if (searchable) {
        suppressOpenOnFocusRef.current = true;
        searchInputRef.current?.focus();
        suppressOpenOnFocusRef.current = false;
      } else {
        comboboxRef.current?.focus();
      }
    };

    const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
      handleKeyDown({
        event: e,
        isOpen,
        disabled,
        options: visibleOptions,
        focusedIndex,
        value,
        onSelect: handleSelect,
        setIsOpen: (open) => {
          if (open) {
            setIsOpen(true);
          } else {
            closeDropdown();
          }
        },
        setFocusedIndex,
        searchable
      });
    };

    const isFixedDropdown = dropdownPlacement === "above";
    const shouldRenderDropdown = isOpen && !disabled && (!isFixedDropdown || dropdownPosition);
    const dropdownStyle: CSSProperties = isFixedDropdown
      ? {
          position: "fixed",
          left: dropdownPosition?.left,
          width: dropdownPosition?.width,
          maxHeight: dropdownPosition?.maxHeight,
          top: dropdownPosition?.top,
          bottom: dropdownPosition?.bottom
        }
      : { width: dropdownRef.current?.getBoundingClientRect().width };
    const dropdownClassName = classNames(
      isFixedDropdown ? "fixed z-[1100]" : "absolute z-20",
      "bg-sys-brand-surface border rounded shadow-lg",
      dropdownHeightStyles[dropdownHeight],
      !isFixedDropdown && "mt-1"
    );

    return (
      <div ref={containerRef} className={classNames("relative w-full", className)}>
        <div className="relative" ref={dropdownRef}>
          <select
            id={searchable ? `${id}-native` : id}
            ref={ref}
            value={value || ""}
            onChange={(e) => handleSelect(e.target.value)}
            className="absolute opacity-0 -z-10 pointer-events-none appearance-none"
            tabIndex={-1}
            disabled={disabled}
            aria-hidden={searchable || undefined}
            name={name ? name : label}
            aria-label={searchable ? undefined : label}
            data-testid={testId}
            role={searchable ? undefined : "combobox"}
            data-value={value}
            aria-invalid={error}
            aria-required={required}
            aria-describedby={
              error && errorText ? `${testId}-error` : supportingText ? `${testId}-supporting` : undefined
            }
          >
            {options.map(({ value: optionValue, label, disabled, displayText }, index) => (
              <option role="option" key={index} value={optionValue} disabled={disabled}>
                {label || displayText || optionValue}
              </option>
            ))}
          </select>

          <div
            ref={comboboxRef}
            onClick={() => {
              if (disabled) return;
              if (searchable) {
                setIsOpen(true);
                searchInputRef.current?.focus();
                return;
              }
              setIsOpen(!isOpen);
            }}
            onKeyDown={searchable ? undefined : onKeyDown}
            aria-expanded={searchable ? undefined : isOpen}
            aria-haspopup={searchable ? undefined : "listbox"}
            aria-controls={searchable ? undefined : listboxId}
            aria-labelledby={searchable || !label ? undefined : `${testId}-label`}
            id={`${testId}-combobox ${name ? name : ""}`}
            tabIndex={searchable || disabled ? -1 : 0}
            aria-label={searchable ? undefined : label}
            aria-invalid={searchable ? undefined : error}
            aria-required={searchable ? undefined : required}
            aria-describedby={
              searchable
                ? undefined
                : error && errorText
                  ? `${testId}-error`
                  : supportingText
                    ? `${testId}-supporting`
                    : undefined
            }
            className={classNames(
              "w-full h-14 px-4 pb-0 pt-1 bg-sys-brand-surface",
              showClearButton && "pr-16",
              "border rounded text-base",
              searchable ? "cursor-text" : "cursor-pointer select-none",
              "focus:outline-none focus:ring-2 focus:ring-sys-brand-primary",
              {
                "border-sys-brand-error hover:border-sys-brand-error/90": error,
                "border-sys-brand-primary": isOpen && !disabled && !error,
                "border-sys-brand-outline hover:border-sys-brand-on-surface-variant": !isOpen && !disabled && !error,
                "border-sys-brand-outline-variant bg-sys-brand-surface-container text-gray-500 !cursor-not-allowed focus:!ring-0":
                  disabled
              }
            )}
          >
            {searchable ? (
              <input
                ref={searchInputRef}
                id={id}
                type="text"
                autoComplete="off"
                disabled={disabled}
                aria-labelledby={label ? `${testId}-label` : undefined}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-controls={isOpen ? listboxId : undefined}
                aria-autocomplete="list"
                aria-activedescendant={
                  isOpen && focusedIndex >= 0 && focusedIndex < visibleOptions.length
                    ? `${id}-option-${focusedIndex}`
                    : undefined
                }
                aria-invalid={error}
                aria-required={required}
                aria-describedby={
                  error && errorText ? `${testId}-error` : supportingText ? `${testId}-supporting` : undefined
                }
                role="combobox"
                data-testid={`${testId}-search`}
                placeholder={isOpen || !selectedLabel ? placeholder : undefined}
                value={isOpen ? searchQuery : selectedLabel}
                onChange={(event) => {
                  const nextQuery = event.target.value;
                  const nextVisible = filterSelectOptions(options, nextQuery);
                  setSearchQuery(nextQuery);
                  setIsOpen(true);
                  setFocusedIndex(nextVisible.length > 0 ? 0 : -1);
                }}
                onFocus={() => {
                  if (suppressOpenOnFocusRef.current) {
                    suppressOpenOnFocusRef.current = false;
                    return;
                  }
                  if (!disabled) setIsOpen(true);
                }}
                onKeyDown={onKeyDown}
                className={classNames(
                  "h-full w-full bg-transparent text-[1rem] outline-none",
                  disabled && "cursor-not-allowed text-gray-500"
                )}
              />
            ) : (
              <div className="h-full flex items-center text-[1rem]">{selectedLabel}</div>
            )}
          </div>

          {label && (
            <label
              id={`${testId}-label`}
              htmlFor={id}
              className={classNames(
                "absolute left-3",
                "-translate-y-1/2",
                "pointer-events-none",
                "bg-sys-brand-surface px-1",
                "transition-all duration-300",
                error ? "!text-sys-brand-error" : "text-sys-brand-on-surface-variant",
                disabled && "text-gray-400",
                ((value?.length ?? 0) > 0 || placeholder || isOpen) && "top-0 text-body-small",
                (value?.length ?? 0) === 0 && !placeholder && !isOpen && "top-7 text-body-large",
                isOpen && !disabled ? "text-sys-brand-primary" : "text-sys-brand-on-surface-variant"
              )}
            >
              {label}
              {required && <span className="text-error ml-0.5">*</span>}
            </label>
          )}

          {placeholder && !searchable && (value?.length ?? 0) === 0 && (
            <span className="absolute text-body-large text-on-surface-variant left-3 top-7 -translate-y-1/2 px-1 pointer-events-none">
              {placeholder}
            </span>
          )}

          {showClearButton ? (
            <button
              type="button"
              aria-label="Clear select"
              data-testid={`${testId}-clear`}
              className="absolute right-10 top-1/2 flex -translate-y-1/2 cursor-pointer text-sys-brand-on-surface-variant"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                handleClear();
              }}
            >
              <Icon name="cancel" />
            </button>
          ) : null}

          <div
            className={classNames(
              "pointer-events-none absolute right-4 top-1/2",
              "border-l-4 border-r-4 border-t-4",
              "border-l-transparent border-r-transparent",
              error ? "border-t-sys-brand-error" : "border-t-sys-brand-on-surface-variant",
              disabled && "border-t-gray-400",
              "w-2 h-2"
            )}
          />

          {shouldRenderDropdown && visibleOptions.length === 0 ? (
            <div id={listboxId} role="status" style={dropdownStyle} className={dropdownClassName}>
              <p className="px-4 py-4 text-[1rem] text-sys-brand-on-surface-variant">No options found</p>
            </div>
          ) : null}

          {shouldRenderDropdown && visibleOptions.length > 0 ? (
            <div id={listboxId} role="listbox" style={dropdownStyle} className={dropdownClassName}>
              {visibleOptions.map(
                ({ disabled: optionDisabled, value: optionValue, label, displayText, border }, index) => (
                  <div
                    key={optionValue}
                    id={`${id}-option-${index}`}
                    ref={(el) => (optionRefs.current[index] = el)}
                    role="option"
                    tabIndex={-1}
                    aria-selected={optionValue === value}
                    data-value={optionValue}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => !optionDisabled && handleSelect(optionValue)}
                    className={classNames(
                      "px-4 py-4 text-[1rem]",
                      "cursor-pointer",
                      !optionDisabled && "hover:bg-sys-brand-surface-container",
                      optionDisabled && "text-gray-400 cursor-not-allowed",
                      border && "border-b border-sys-brand-surface-dim",
                      focusedIndex === index && "bg-sys-brand-surface-container",
                      optionValue === value && "bg-sys-brand-surface-container-high"
                    )}
                  >
                    {label || displayText || optionValue}
                  </div>
                )
              )}
            </div>
          ) : null}
        </div>

        {error && errorText && (
          <p id={`${testId}-error`} className="mt-1 ml-3 text-body-small text-sys-brand-error" role="alert">
            {errorText}
          </p>
        )}
        {supportingText && !error && (
          <p id={`${testId}-supporting`} className="mt-1 ml-3 text-body-small text-sys-brand-on-surface-variant">
            {supportingText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = "Select";

export default Select;
